import { useSignIn } from '@clerk/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { EnvelopeSimple } from '@phosphor-icons/react';
import { Button, Divider, Field, Link, TextInput } from '@reelplicate/ui';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router';

import { LEGAL_LINKS, marketingUrl } from '@/lib/marketing';

import { AuthBody, AuthCard, AuthFooter, AuthHeader } from './auth-card';
import { AuthToast } from './auth-toast';
import { describeClerkError } from './clerk-errors';
import { OAuthButtons } from './oauth-buttons';
import { PasswordInput } from './password-input';
import { useRedirectTarget, withRedirect } from './redirect-target';
import { SignInSchema, type SignInValues } from './schemas';

type Failure = { title: string; description: string; credentials: boolean };

const WRONG_CREDENTIALS = new Set([
  'form_password_incorrect',
  'form_identifier_not_found',
  'form_password_validation_failed',
]);

function toFailure(error: unknown): Failure {
  const { code, message } = describeClerkError(error);
  if (WRONG_CREDENTIALS.has(code)) {
    return {
      title: 'Incorrect email or password',
      description: 'Check both and try again, or reset your password.',
      credentials: true,
    };
  }
  return { title: 'Couldn’t sign you in', description: message, credentials: false };
}

/**
 * Figma "Auth B — Sign in" (214:1097). A failed attempt shows the "Sign in —
 * error" state (214:1181): the error toast at the top and, for wrong
 * credentials, both fields marked invalid.
 */
export function SignInPage() {
  const navigate = useNavigate();
  const redirectTo = useRedirectTarget();
  const { signIn } = useSignIn();
  const [failure, setFailure] = useState<Failure | null>(null);
  const form = useForm<SignInValues>({
    resolver: zodResolver(SignInSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = form.handleSubmit(async ({ email, password }) => {
    setFailure(null);
    const { error } = await signIn.password({ identifier: email, password });
    if (error) {
      setFailure(toFailure(error));
      return;
    }
    if (signIn.status !== 'complete') {
      // Two-factor and new-device checks are off in Clerk until those screens
      // are wired; if one is turned on, say so rather than hang.
      setFailure({
        title: 'Another step is needed',
        description: 'This account needs a verification step we don’t support yet.',
        credentials: false,
      });
      return;
    }
    await signIn.finalize({ navigate: () => navigate(redirectTo, { replace: true }) });
  });

  const invalid = failure?.credentials || undefined;

  return (
    <>
      {failure && (
        <AuthToast
          title={failure.title}
          description={failure.description}
          onDismiss={() => setFailure(null)}
        />
      )}
      <AuthCard>
        <AuthHeader title="Sign in" subtitle="Sign in to keep your reels moving." />
        <form onSubmit={onSubmit} noValidate>
          <AuthBody>
            <OAuthButtons action="Continue" />
            <Divider tone="subtle" label="or continue with email" />
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field label="Email" error={fieldState.error?.message}>
                  <TextInput
                    {...field}
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    leadingIcon={EnvelopeSimple}
                    autoFocus
                    aria-invalid={invalid}
                  />
                </Field>
              )}
            />
            <Controller
              name="password"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field label="Password" error={fieldState.error?.message}>
                  <PasswordInput
                    {...field}
                    reveal
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    aria-invalid={invalid}
                  />
                </Field>
              )}
            />
            <div className="flex justify-end">
              <Link asChild size="sm">
                <RouterLink to="/forgot-password">Forgot password?</RouterLink>
              </Link>
            </div>
            <Button
              type="submit"
              size="lg"
              className="w-full"
              loading={form.formState.isSubmitting}
            >
              Sign in
            </Button>
          </AuthBody>
        </form>
        <AuthFooter>
          Don’t have an account?
          <Link asChild>
            <RouterLink to={withRedirect('/sign-up', redirectTo)}>Sign up</RouterLink>
          </Link>
        </AuthFooter>
        {!failure && (
          <p className="text-caption text-text-tertiary text-center">
            By continuing, you agree to our{' '}
            <a
              href={marketingUrl(LEGAL_LINKS.terms)}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Terms
            </a>{' '}
            and{' '}
            <a
              href={marketingUrl(LEGAL_LINKS.privacy)}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Privacy policy
            </a>
            .
          </p>
        )}
      </AuthCard>
    </>
  );
}
